/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { get as userGet } from 'js/lib/user';

import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import CourseraPlusButtonListItem from 'bundles/composable-page-header/components/coursera-plus-button/CourseraPlusButtonListItem';
import MobileMenuConfigWrapper from 'bundles/composable-page-header/components/grid-items/mobile/MobileMenuConfigWrapper';
import AccountMenuItem from 'bundles/composable-page-header/components/mobile-menu-items/AccountMenuItem';
import AccountMenuItemExpanded from 'bundles/composable-page-header/components/mobile-menu-items/AccountMenuItemExpanded';
import AccountSubMenu from 'bundles/composable-page-header/components/mobile-menu-items/AccountSubMenu';
import LanguageTranslationSubMenu from 'bundles/composable-page-header/components/mobile-menu-items/LanguageTranslationSubMenu';
import MobileAuthMenuItem from 'bundles/composable-page-header/components/mobile-menu-items/MobileAuthMenuItem';
import ProgramList from 'bundles/composable-page-header/components/program-switcher/ProgramList';
import { MOBILE_MAIN_IDENTIFIER, logInButtonProps, signUpButtonProps } from 'bundles/composable-page-header/constants';
import type { AuthButtonProps } from 'bundles/composable-page-header/types';
import getDefaultAccountDropdownLinks from 'bundles/composable-page-header/utils/getDefaultAccountDropdownLinks';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';

const styles = {
  borderOverride: css`
    button:only-of-type {
      border-bottom: none;
      margin-bottom: 0;
    }
  `,
};

type Props = {
  acctDropdownLogoutOnly?: boolean;
  showProgramSwitcher?: boolean;
  showLanguageDropdown?: boolean;
  showAcctDropdown?: boolean;
  showAcctItemsInMain?: boolean;
  showSignupBtn?: boolean;
  showLoginBtn?: boolean;
};

const SimpleMobileMenuConfig = ({
  acctDropdownLogoutOnly,
  showProgramSwitcher,
  showLanguageDropdown,
  showAcctDropdown,
  showAcctItemsInMain,
  showSignupBtn,
  showLoginBtn,
}: Props) => {
  const consumerNavRefresh = GrowthDiscoveryEpicClient.get('consumerNavRefresh');
  const {
    hasEnrolledPrograms,
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
    chosenDegree,
    chosenEnterpriseProgram,
    isCourseraPlusLearner,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    courseraPlusSubscriptionCycle,
  } = useConsumerMyPageHeader();

  const unauthActionButtons: AuthButtonProps[] = [];
  if (showSignupBtn) unauthActionButtons.push(signUpButtonProps());
  if (showLoginBtn) unauthActionButtons.push(logInButtonProps());

  const isLoggedIn = userGet()?.id;

  if (isLoggedIn) {
    return (
      <MobileMenuConfigWrapper
        subPagesMap={{
          ...(!showAcctItemsInMain &&
            (showAcctDropdown || acctDropdownLogoutOnly) && {
              [MOBILE_MAIN_IDENTIFIER.ACCOUNT_MENU]: (
                <AccountSubMenu
                  dropdownLinks={
                    acctDropdownLogoutOnly
                      ? []
                      : getDefaultAccountDropdownLinks({
                          isDegreeOrEnterpriseLearner: !!hasEnrolledPrograms,
                          managedThirdPartyOrganizations: naptimeManagedThirdPartyOrganizations,
                          canViewEducatorAdminLink,
                          hasCheatingIncidents,
                        })
                  }
                  courseraPlusSubscriptionCycle={courseraPlusSubscriptionCycle}
                  hideLanguage
                  noBackButton={consumerNavRefresh}
                />
              ),
            }),
          ...(showLanguageDropdown && {
            [MOBILE_MAIN_IDENTIFIER.LANGUAGE_MENU]: (
              <LanguageTranslationSubMenu noBackButton={consumerNavRefresh} backToMain={showAcctItemsInMain} />
            ),
          }),
        }}
        navExperiment={consumerNavRefresh}
      >
        <div css={styles.borderOverride}>
          {showAcctItemsInMain ? (
            <AccountMenuItemExpanded
              dropdownLinks={
                acctDropdownLogoutOnly
                  ? []
                  : getDefaultAccountDropdownLinks({
                      isDegreeOrEnterpriseLearner: !!hasEnrolledPrograms,
                      managedThirdPartyOrganizations: naptimeManagedThirdPartyOrganizations,
                      canViewEducatorAdminLink,
                      hasCheatingIncidents,
                    })
              }
              hasCourseraPlus={courseraPlusSubscriptionCycle != null}
              noBottomBorder={consumerNavRefresh}
              hideLanguage
            />
          ) : (
            <AccountMenuItem
              hasCourseraPlus={courseraPlusSubscriptionCycle != null}
              noBottomBorder={consumerNavRefresh}
            />
          )}
        </div>
        {!acctDropdownLogoutOnly && !hasEnrolledPrograms && !isCourseraPlusLearner && <CourseraPlusButtonListItem />}

        {showProgramSwitcher && hasEnrolledPrograms && (
          <ProgramList
            currentProgram={chosenEnterpriseProgram}
            currentDegree={chosenDegree}
            programs={enrolledEnterprisePrograms || []}
            degrees={enrolledDegreePrograms || []}
            alwaysShowPersonalAccount
            trackingName="switcher_dropdown_program_option"
          />
        )}
      </MobileMenuConfigWrapper>
    );
  } else {
    return (
      <MobileMenuConfigWrapper subPagesMap={{}} navExperiment={consumerNavRefresh}>
        <CourseraPlusButtonListItem showTopBorder={false} />
        <MobileAuthMenuItem buttons={unauthActionButtons} />
      </MobileMenuConfigWrapper>
    );
  }
};

export default SimpleMobileMenuConfig;
